import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertCircle,
  BadgeCheck,
  CheckCircle2,
  ClipboardList,
  Eye,
  FilterX,
  IdCard,
  Info,
  Loader2,
  RefreshCw,
  Search,
  Shield,
  UserCheck,
  Users,
  WalletCards,
} from "lucide-react";
import api from "../../api";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

interface EventOption {
  id: string;
  name: string;
}

interface SummaryResponse {
  total: number;
  attendees: number;
  speakers: number;
  pendingPayment: number;
  syswebPaid: number;
  noCostConfirmed: number;
  credentialsIssued: number;
  mexican: number;
  foreign: number;
  unachStudents: number;
  specialNeeds: number;
  hasAssignedEvents: boolean;
}

interface RegistrationMonitorItem {
  id: string;
  folio: string;
  name: string;
  event: { id: string; name: string; shortName: string | null };
  type: "ATTENDEE" | "SPEAKER";
  nationality: string;
  validation: { code: string; label: string };
  modality: string;
  status: string;
  paymentStatus: { code: string; label: string };
  qrStatus: { code: string; label: string };
  hasSpecialNeeds: boolean;
  updatedAt: string;
}

interface ListResponse {
  items: RegistrationMonitorItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasAssignedEvents: boolean;
}

interface RegistrationDetail {
  id: string;
  folio: string;
  event: { id: string; name: string; shortName: string | null };
  general: { type: string; status: string; createdAt: string; updatedAt: string };
  contact: {
    name: string;
    email: string;
    phone: string | null;
    nationality: string;
    country: string;
    state: string;
    city: string;
    gender: string;
    age: number;
    curpMasked: string | null;
  };
  participation: {
    institution: string;
    enrollment: string | null;
    validation: { code: string; label: string };
    validatedAt: string | null;
    isUnachStudent: boolean;
    participantType: string;
    modality: string;
    braceletCode: string | null;
    hasSpecialNeeds: boolean;
    healthData: {
      conditions: string[];
      detailsImportant: string | null;
      otherDetails: string | null;
      emergencyContactName: string | null;
      emergencyContactPhone: string | null;
      bloodType: string | null;
      medicalAuthorization: boolean | null;
    } | null;
  };
  payment: {
    status: { code: string; label: string };
    source: { code: string; label: string };
    paidAt: string | null;
    paymentAmount: string | null;
    syswebReference: string | null;
    syswebTransactionIdTruncated: string | null;
    braceletCodeMasked: string | null;
    jobs: Array<{
      id: string;
      jobType: string;
      status: string;
      attemptCount: number;
      maxAttempts: number;
      nextAttemptAt: string | null;
      lastAttemptAt: string | null;
      completedAt: string | null;
      lastErrorPreview: string | null;
    }>;
  };
  credential: {
    qrStatus: { code: string; label: string };
    credentialSentAt: string | null;
  };
  paper: {
    title: string | null;
    area: string | null;
    registrationStatus: string;
    evaluationStatus: string | null;
    currentRound: number | null;
    materialStatus: string;
  } | null;
  timeline: Array<{ label: string; at: string | null; status?: string }>;
}

interface RegistrationMonitorPageProps {
  canView: boolean;
  selectedEventId: string;
  availableEvents: EventOption[];
}

const primaryButtonClass = "bg-[#1e3a5f] text-white hover:bg-[#0f2744] hover:text-white disabled:text-white";

const statusLabels: Record<string, string> = {
  PENDING: "Pendiente",
  PENDING_REVIEW: "Revision academica",
  REVISION_REQUESTED: "Correccion solicitada",
  APPROVED_TO_PAY: "Aprobado para pago",
  REJECTED: "Rechazado",
  PAID: "Pagado",
  MATERIAL_UPLOADED: "Material recibido",
  CREDENTIAL_ISSUED: "Credencial emitida",
};

const statusBadgeClasses: Record<string, string> = {
  PENDING: "border-amber-200 bg-amber-50 text-amber-700",
  PENDING_REVIEW: "border-blue-200 bg-blue-50 text-blue-700",
  REVISION_REQUESTED: "border-orange-200 bg-orange-50 text-orange-700",
  APPROVED_TO_PAY: "border-emerald-200 bg-emerald-50 text-emerald-700",
  REJECTED: "border-red-200 bg-red-50 text-red-700",
  PAID: "border-green-200 bg-green-50 text-green-700",
  MATERIAL_UPLOADED: "border-teal-200 bg-teal-50 text-teal-700",
  CREDENTIAL_ISSUED: "border-indigo-200 bg-indigo-50 text-indigo-700",
};

const paymentBadgeClasses: Record<string, string> = {
  PENDING_PAYMENT: "border-amber-200 bg-amber-50 text-amber-700",
  SYSWEB_PAID: "border-emerald-200 bg-emerald-50 text-emerald-700",
  PAID: "border-emerald-200 bg-emerald-50 text-emerald-700",
  NOT_REQUIRED: "border-slate-200 bg-slate-50 text-slate-700",
  REJECTED: "border-red-200 bg-red-50 text-red-700",
};

const validationBadgeClasses: Record<string, string> = {
  SIAE_VALIDATED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  MANUAL: "border-blue-200 bg-blue-50 text-blue-700",
  NOT_VALIDATED: "border-slate-200 bg-slate-50 text-slate-700",
};

function formatDate(value: string | null | undefined, includeTime = true): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: includeTime ? undefined : "numeric",
    hour: includeTime ? "2-digit" : undefined,
    minute: includeTime ? "2-digit" : undefined,
  }).format(date);
}

function statusLabel(value: string): string {
  return statusLabels[value] || value;
}

function syswebJobStatusLabel(status: string, lastErrorPreview?: string | null): string {
  const normalizedReason = lastErrorPreview?.trim();

  if (status === "SKIPPED") {
    if (
      normalizedReason === "REGISTRATION_ALREADY_RESOLVED" ||
      normalizedReason === "REGISTRATION_ALREADY_RESOLVED_MANUALLY"
    ) {
      return "Omitido: registro ya resuelto";
    }
    if (normalizedReason === "MANUAL_PAYMENT_CONFIRMED") return "Omitido por pago manual";
    if (normalizedReason === "RESOLVED_BY_SYSWEB_CALLBACK") return "Omitido: resuelto por callback SYSWEB";
    return "Omitido";
  }

  return status;
}

function KpiCard({
  title,
  value,
  icon: Icon,
  tone,
}: {
  title: string;
  value: number;
  icon: typeof ClipboardList;
  tone: string;
}) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardContent className="flex min-h-[92px] items-center justify-between gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-[#0f2744]">{title}</p>
          <p className="mt-1 text-2xl font-bold text-[#0f2744]">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ value }: { value: string }) {
  return <Badge variant="outline" className={statusBadgeClasses[value] || "border-gray-200 bg-gray-50 text-gray-700"}>{statusLabel(value)}</Badge>;
}

function PaymentBadge({ status }: { status: { code: string; label: string } }) {
  return <Badge variant="outline" className={paymentBadgeClasses[status.code] || "border-gray-200 bg-gray-50 text-gray-700"}>{status.label}</Badge>;
}

function ValidationBadge({ status }: { status: { code: string; label: string } }) {
  return <Badge variant="outline" className={validationBadgeClasses[status.code] || "border-gray-200 bg-gray-50 text-gray-700"}>{status.label}</Badge>;
}

export function RegistrationMonitorPage({ canView, selectedEventId, availableEvents }: RegistrationMonitorPageProps) {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [registrations, setRegistrations] = useState<RegistrationMonitorItem[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [eventId, setEventId] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [paymentStatus, setPaymentStatus] = useState("ALL");
  const [modality, setModality] = useState("ALL");
  const [nationality, setNationality] = useState("ALL");
  const [validation, setValidation] = useState("ALL");
  const [specialNeeds, setSpecialNeeds] = useState("ALL");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RegistrationDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  useEffect(() => {
    if (selectedEventId && availableEvents.length === 1) {
      setEventId(selectedEventId);
    }
  }, [availableEvents.length, selectedEventId]);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page,
      pageSize,
      sortBy: "updatedAt",
      sortDirection: "desc",
    };
    if (eventId !== "ALL") params.eventId = eventId;
    if (type !== "ALL") params.type = type;
    if (status !== "ALL") params.status = status;
    if (paymentStatus !== "ALL") params.paymentStatus = paymentStatus;
    if (modality !== "ALL") params.modality = modality;
    if (nationality !== "ALL") params.nationality = nationality;
    if (validation !== "ALL") params.validation = validation;
    if (specialNeeds !== "ALL") params.specialNeeds = specialNeeds;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (q.trim().length >= 3) params.q = q.trim();
    return params;
  }, [dateFrom, dateTo, eventId, modality, nationality, page, pageSize, paymentStatus, q, specialNeeds, status, type, validation]);

  const summaryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (eventId !== "ALL") params.eventId = eventId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return params;
  }, [dateFrom, dateTo, eventId]);

  const loadMonitor = useCallback(async () => {
    if (!canView) return;
    setIsLoading(true);
    setError(null);
    try {
      const [summaryResponse, listResponse] = await Promise.all([
        api.get<SummaryResponse>("/admin/registrations-monitor/summary", { params: summaryParams, headers: { "X-Passive-Request": "true" } }),
        api.get<ListResponse>("/admin/registrations-monitor", { params: queryParams, headers: { "X-Passive-Request": "true" } }),
      ]);
      setSummary(summaryResponse.data);
      setRegistrations(listResponse.data.items);
      setTotal(listResponse.data.total);
      setTotalPages(listResponse.data.totalPages);
    } catch {
      setError("No se pudo cargar el monitor.");
      setRegistrations([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [canView, queryParams, summaryParams]);

  useEffect(() => {
    loadMonitor();
  }, [loadMonitor]);

  const loadDetail = async (registrationId: string) => {
    setDetailId(registrationId);
    setIsDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get<RegistrationDetail>(`/admin/registrations-monitor/${registrationId}`, {
        headers: { "X-Passive-Request": "true" },
      });
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const clearFilters = () => {
    setEventId(availableEvents.length === 1 && selectedEventId ? selectedEventId : "ALL");
    setType("ALL");
    setStatus("ALL");
    setPaymentStatus("ALL");
    setModality("ALL");
    setNationality("ALL");
    setValidation("ALL");
    setSpecialNeeds("ALL");
    setQ("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasShortSearch = q.trim().length > 0 && q.trim().length < 3;
  const hasFilters = eventId !== "ALL" || type !== "ALL" || status !== "ALL" || paymentStatus !== "ALL" || modality !== "ALL" || nationality !== "ALL" || validation !== "ALL" || specialNeeds !== "ALL" || q.trim().length >= 3 || dateFrom || dateTo;
  const hasAssignedEvents = summary?.hasAssignedEvents ?? availableEvents.length > 0;

  if (!canView) {
    return (
      <EmptyState
        icon={<Shield className="h-16 w-16 text-slate-500" />}
        title="Acceso restringido"
        description="No tienes permisos para visualizar el Monitor de Registros."
      />
    );
  }

  if (!hasAssignedEvents && !isLoading && !error) {
    return (
      <div className="space-y-5">
        <PageHeader onRefresh={loadMonitor} isLoading={isLoading} />
        <EmptyState
          icon={<ClipboardList className="h-16 w-16 text-slate-400" />}
          title="Sin eventos asignados"
          description="No tienes eventos asignados para monitorear registros."
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader onRefresh={loadMonitor} isLoading={isLoading} />

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <KpiCard title="Total de registros" value={summary?.total ?? 0} icon={ClipboardList} tone="bg-blue-100 text-blue-700" />
        <KpiCard title="Asistentes" value={summary?.attendees ?? 0} icon={Users} tone="bg-sky-100 text-sky-700" />
        <KpiCard title="Ponentes" value={summary?.speakers ?? 0} icon={UserCheck} tone="bg-indigo-100 text-indigo-700" />
        <KpiCard title="Pendientes de pago" value={summary?.pendingPayment ?? 0} icon={WalletCards} tone="bg-amber-100 text-amber-700" />
        <KpiCard title="Pagos SYSWEB" value={summary?.syswebPaid ?? 0} icon={CheckCircle2} tone="bg-emerald-100 text-emerald-700" />
        <KpiCard title="Sin costo / validados" value={summary?.noCostConfirmed ?? 0} icon={BadgeCheck} tone="bg-slate-100 text-slate-700" />
        <KpiCard title="Credenciales emitidas" value={summary?.credentialsIssued ?? 0} icon={IdCard} tone="bg-violet-100 text-violet-700" />
        <KpiCard title="Mexicanos" value={summary?.mexican ?? 0} icon={BadgeCheck} tone="bg-green-100 text-green-700" />
        <KpiCard title="Extranjeros" value={summary?.foreign ?? 0} icon={Users} tone="bg-slate-100 text-slate-700" />
        <KpiCard title="Estudiantes UNACH" value={summary?.unachStudents ?? 0} icon={UserCheck} tone="bg-teal-100 text-teal-700" />
        <KpiCard title="Necesidades especiales" value={summary?.specialNeeds ?? 0} icon={Info} tone="bg-orange-100 text-orange-700" />
      </div>

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-4">
          <div className="grid gap-4 lg:grid-cols-4">
            <FilterSelect label="Evento" value={eventId} onChange={(value) => { setEventId(value); setPage(1); }}>
              <SelectItem value="ALL">Todos los eventos</SelectItem>
              {availableEvents.map((event) => <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>)}
            </FilterSelect>
            <FilterSelect label="Tipo" value={type} onChange={(value) => { setType(value); setPage(1); }}>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="ATTENDEE">Asistente</SelectItem>
              <SelectItem value="SPEAKER">Ponente</SelectItem>
            </FilterSelect>
            <FilterSelect label="Estado" value={status} onChange={(value) => { setStatus(value); setPage(1); }}>
              <SelectItem value="ALL">Todos</SelectItem>
              {Object.keys(statusLabels).map((option) => <SelectItem key={option} value={option}>{statusLabel(option)}</SelectItem>)}
            </FilterSelect>
            <FilterSelect label="Estado de pago" value={paymentStatus} onChange={(value) => { setPaymentStatus(value); setPage(1); }}>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="PENDING_PAYMENT">Pendiente de pago</SelectItem>
              <SelectItem value="SYSWEB_PAID">Pagado SYSWEB</SelectItem>
              <SelectItem value="NOT_REQUIRED">Sin costo / validado</SelectItem>
              <SelectItem value="REJECTED">Rechazado</SelectItem>
            </FilterSelect>
            <FilterSelect label="Modalidad" value={modality} onChange={(value) => { setModality(value); setPage(1); }}>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="GENERAL_CON_KIT">Kit y constancia</SelectItem>
              <SelectItem value="GENERAL_SIN_KIT">Constancia</SelectItem>
              <SelectItem value="ASISTENTE_UNACH">Asistente UNACH</SelectItem>
              <SelectItem value="OYENTE_UNACH">Oyente UNACH</SelectItem>
              <SelectItem value="docente-unach">Docente UNACH</SelectItem>
              <SelectItem value="docente-externo">Docente externo</SelectItem>
            </FilterSelect>
            <FilterSelect label="Nacionalidad" value={nationality} onChange={(value) => { setNationality(value); setPage(1); }}>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="MEXICAN">Mexicana</SelectItem>
              <SelectItem value="FOREIGN">Extranjera</SelectItem>
            </FilterSelect>
            <FilterSelect label="Validacion" value={validation} onChange={(value) => { setValidation(value); setPage(1); }}>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="SIAE_VALIDATED">SIAE</SelectItem>
              <SelectItem value="MANUAL">Manual</SelectItem>
              <SelectItem value="NOT_VALIDATED">Sin validar</SelectItem>
            </FilterSelect>
            <FilterSelect label="Necesidades especiales" value={specialNeeds} onChange={(value) => { setSpecialNeeds(value); setPage(1); }}>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="true">Con necesidades especiales</SelectItem>
              <SelectItem value="false">Sin necesidades especiales</SelectItem>
            </FilterSelect>
            <div className="space-y-2">
              <Label>Buscar por folio o nombre</Label>
              <div className="relative">
                <Input value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} placeholder="Buscar por folio o nombre..." className="pr-9" />
                <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              {hasShortSearch && <p className="text-xs text-amber-700">Escribe al menos 3 caracteres para buscar.</p>}
            </div>
            <div className="space-y-2">
              <Label>Desde</Label>
              <Input type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); setPage(1); }} />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <Input type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); setPage(1); }} />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={clearFilters}>
              <FilterX className="mr-2 h-4 w-4" />
              Limpiar filtros
            </Button>
            <Button onClick={loadMonitor} disabled={isLoading} className={primaryButtonClass}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#0f2744]">Registros recientes</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <EmptyState
              icon={<AlertCircle className="h-16 w-16 text-red-500" />}
              title="No se pudo cargar el monitor"
              description="Intenta actualizar la informacion. Si el problema persiste, revisa los logs del servidor."
              action={<Button className={primaryButtonClass} onClick={loadMonitor}>Reintentar carga</Button>}
            />
          ) : isLoading ? (
            <div className="flex min-h-[260px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
            </div>
          ) : registrations.length === 0 ? (
            <EmptyState
              icon={<ClipboardList className="h-16 w-16 text-slate-400" />}
              title={hasFilters ? "No se encontraron resultados" : "Sin registros disponibles"}
              description={hasFilters ? "No hay registros que coincidan con los filtros seleccionados." : "Cuando existan asistentes o ponentes registrados, apareceran aqui."}
              action={
                <div className="flex gap-2">
                  {hasFilters && <Button variant="outline" onClick={clearFilters}>Limpiar filtros</Button>}
                  <Button className={primaryButtonClass} onClick={loadMonitor}>Actualizar</Button>
                </div>
              }
            />
          ) : (
            <>
              <div className="max-h-[62vh] max-w-full overflow-auto rounded-md border border-slate-200 bg-white">
                <table className="w-full min-w-[1900px] border-separate border-spacing-0 table-fixed text-sm">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="sticky left-0 top-0 z-50 h-10 w-[185px] min-w-[185px] border-b border-r border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[5px_0_10px_-8px_rgba(15,23,42,0.65),0_1px_0_rgba(15,23,42,0.08)]">Folio</th>
                      <th className="sticky top-0 z-40 h-10 w-[210px] min-w-[210px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Nombre</th>
                      <th className="sticky top-0 z-40 h-10 w-[260px] min-w-[260px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Evento</th>
                      <th className="sticky top-0 z-40 h-10 w-[120px] min-w-[120px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Tipo</th>
                      <th className="sticky top-0 z-40 h-10 w-[130px] min-w-[130px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Nacionalidad</th>
                      <th className="sticky top-0 z-40 h-10 w-[140px] min-w-[140px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Validacion</th>
                      <th className="sticky top-0 z-40 h-10 w-[190px] min-w-[190px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Modalidad</th>
                      <th className="sticky top-0 z-40 h-10 w-[160px] min-w-[160px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Estado</th>
                      <th className="sticky top-0 z-40 h-10 w-[170px] min-w-[170px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Pago</th>
                      <th className="sticky top-0 z-40 h-10 w-[120px] min-w-[120px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">QR</th>
                      <th className="sticky top-0 z-40 h-10 w-[130px] min-w-[130px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Necesidades</th>
                      <th className="sticky top-0 z-40 h-10 w-[150px] min-w-[150px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Actualizado</th>
                      <th className="sticky top-0 z-40 h-10 w-[110px] min-w-[110px] border-b border-slate-200 bg-slate-50 px-3 text-left align-middle text-[11px] font-bold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(15,23,42,0.08)]">Accion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((registration) => (
                      <tr key={registration.id} className="group hover:bg-blue-50/30">
                        <td className="sticky left-0 z-10 w-[185px] min-w-[185px] whitespace-nowrap border-b border-r border-slate-100 bg-white px-3 py-3 font-medium text-[#0f2744] shadow-[5px_0_10px_-8px_rgba(15,23,42,0.55)] group-hover:bg-blue-50/80">{registration.folio}</td>
                        <td className="w-[210px] min-w-[210px] truncate border-b border-slate-100 px-3 py-3" title={registration.name}>{registration.name}</td>
                        <td className="w-[260px] min-w-[260px] truncate border-b border-slate-100 px-3 py-3" title={registration.event.name}>{registration.event.shortName || registration.event.name}</td>
                        <td className="w-[120px] min-w-[120px] border-b border-slate-100 px-3 py-3">{registration.type === "SPEAKER" ? "Ponente" : "Asistente"}</td>
                        <td className="w-[130px] min-w-[130px] border-b border-slate-100 px-3 py-3">{registration.nationality}</td>
                        <td className="w-[140px] min-w-[140px] border-b border-slate-100 px-3 py-3"><ValidationBadge status={registration.validation} /></td>
                        <td className="w-[190px] min-w-[190px] truncate border-b border-slate-100 px-3 py-3" title={registration.modality}>{registration.modality}</td>
                        <td className="w-[160px] min-w-[160px] border-b border-slate-100 px-3 py-3"><StatusBadge value={registration.status} /></td>
                        <td className="w-[170px] min-w-[170px] border-b border-slate-100 px-3 py-3"><PaymentBadge status={registration.paymentStatus} /></td>
                        <td className="w-[120px] min-w-[120px] border-b border-slate-100 px-3 py-3">{registration.qrStatus.label}</td>
                        <td className="w-[130px] min-w-[130px] border-b border-slate-100 px-3 py-3">
                          <Badge variant="outline" className={registration.hasSpecialNeeds ? "border-orange-200 bg-orange-50 text-orange-700" : "border-gray-200 bg-gray-50 text-gray-600"}>
                            {registration.hasSpecialNeeds ? "Si" : "No"}
                          </Badge>
                        </td>
                        <td className="w-[150px] min-w-[150px] whitespace-nowrap border-b border-slate-100 px-3 py-3">{formatDate(registration.updatedAt)}</td>
                        <td className="w-[110px] min-w-[110px] border-b border-slate-100 px-3 py-3">
                          <Button variant="outline" size="sm" onClick={() => loadDetail(registration.id)}>
                            <Eye className="mr-1 h-3.5 w-3.5" />
                            Ver
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-600">Mostrando {registrations.length} de {total} registros</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => Math.max(value - 1, 1))}>Anterior</Button>
                  <span className="text-sm text-gray-600">Pagina {page} de {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>Siguiente</Button>
                  <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                    <SelectTrigger className="w-[90px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Alert className="border-blue-200 bg-white text-blue-900">
        <Shield className="h-4 w-4" />
        <AlertDescription>La lista omite correo, telefono, CURP, tokens, rutas internas y URLs de pago. El detalle solo muestra datos operativos autorizados.</AlertDescription>
      </Alert>

      <RegistrationDetailSheet
        detailId={detailId}
        detail={detail}
        isLoading={isDetailLoading}
        onClose={() => { setDetailId(null); setDetail(null); }}
      />
    </div>
  );
}

function PageHeader({ onRefresh, isLoading }: { onRefresh: () => void; isLoading: boolean }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#0f2744]">Monitor de Registros</h1>
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Solo lectura</Badge>
          </div>
          <p className="mt-1 text-sm text-gray-600">Seguimiento operativo de asistentes y ponentes por evento.</p>
        </div>
        <Button onClick={onRefresh} disabled={isLoading} className={primaryButtonClass}>
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Actualizar
        </Button>
      </div>
      <Alert className="border-blue-200 bg-blue-50 text-blue-900">
        <Info className="h-4 w-4" />
        <AlertDescription>Este monitor es de solo lectura. No modifica registros, pagos, credenciales ni correos.</AlertDescription>
      </Alert>
    </div>
  );
}

function FilterSelect({ label, value, onChange, children }: { label: string; value: string; onChange: (value: string) => void; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}

function EmptyState({ icon, title, description, action }: { icon: ReactNode; title: string; description: string; action?: ReactNode }) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardContent className="flex min-h-[360px] flex-col items-center justify-center p-8 text-center">
        <div className="mb-5">{icon}</div>
        <h2 className="text-xl font-bold text-[#0f2744]">{title}</h2>
        <p className="mt-2 max-w-md text-sm text-gray-600">{description}</p>
        {action && <div className="mt-5">{action}</div>}
      </CardContent>
    </Card>
  );
}

function RegistrationDetailSheet({
  detailId,
  detail,
  isLoading,
  onClose,
}: {
  detailId: string | null;
  detail: RegistrationDetail | null;
  isLoading: boolean;
  onClose: () => void;
}) {
  return (
    <Sheet open={Boolean(detailId)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-3xl xl:max-w-4xl">
        <SheetHeader>
          <SheetTitle className="text-xl text-[#0f2744]">Detalle de registro</SheetTitle>
          <SheetDescription>Informacion operativa de solo lectura</SheetDescription>
        </SheetHeader>
        {isLoading ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
          </div>
        ) : detail ? (
          <div className="space-y-4 px-4 pb-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-[#0f2744]">{detail.folio}</span>
              <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">{detail.general.type === "SPEAKER" ? "Ponente" : "Asistente"}</Badge>
              <StatusBadge value={detail.general.status} />
              <PaymentBadge status={detail.payment.status} />
            </div>

            <DetailSection title="1. General">
              <DetailGrid rows={[
                ["Folio", detail.folio],
                ["Evento", detail.event.name],
                ["Tipo", detail.general.type === "SPEAKER" ? "Ponente" : "Asistente"],
                ["Estado actual", statusLabel(detail.general.status)],
                ["Fecha de registro", formatDate(detail.general.createdAt)],
                ["Ultima actualizacion", formatDate(detail.general.updatedAt)],
              ]} />
            </DetailSection>

            <DetailSection title="2. Contacto">
              <DetailGrid rows={[
                ["Nombre completo", detail.contact.name],
                ["Correo", detail.contact.email],
                ["Telefono", detail.contact.phone || "-"],
                ["Nacionalidad", detail.contact.nationality],
                ["Ubicacion", [detail.contact.country, detail.contact.state, detail.contact.city].filter(Boolean).join(", ")],
                ["Genero / edad", `${detail.contact.gender} / ${detail.contact.age}`],
                ["CURP", detail.contact.curpMasked || "-"],
              ]} />
            </DetailSection>

            <DetailSection title="3. Participacion">
              <DetailGrid rows={[
                ["Institucion", detail.participation.institution],
                ["Matricula", detail.participation.enrollment || "-"],
                ["Validacion", detail.participation.validation.label],
                ["Validado en", formatDate(detail.participation.validatedAt)],
                ["Estudiante UNACH", detail.participation.isUnachStudent ? "Si" : "No"],
                ["Tipo participante", detail.participation.participantType],
                ["Modalidad", detail.participation.modality],
                ["Codigo brazalete", detail.participation.braceletCode || "-"],
              ]} />
              <div className="mt-4 rounded-md border border-gray-200 p-3">
                <p className="text-sm font-semibold text-[#0f2744]">Necesidades especiales</p>
                {detail.participation.healthData ? (
                  <div className="mt-2 space-y-2 text-sm text-gray-700">
                    <p>Condiciones: {detail.participation.healthData.conditions.length ? detail.participation.healthData.conditions.join(", ") : "Ninguna"}</p>
                    {detail.participation.healthData.detailsImportant && <p className="whitespace-pre-wrap">Detalle: {detail.participation.healthData.detailsImportant}</p>}
                    {detail.participation.healthData.otherDetails && <p className="whitespace-pre-wrap">Otros: {detail.participation.healthData.otherDetails}</p>}
                    <p>Contacto emergencia: {detail.participation.healthData.emergencyContactName || "-"} / {detail.participation.healthData.emergencyContactPhone || "-"}</p>
                    <p>Tipo de sangre: {detail.participation.healthData.bloodType || "-"}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">Condiciones: Ninguna</p>
                )}
              </div>
            </DetailSection>

            <DetailSection title="4. Pago / SYSWEB">
              <DetailGrid rows={[
                ["Estado de pago", detail.payment.status.label],
                ["Fuente", detail.payment.source.label],
                ["Fecha de pago", formatDate(detail.payment.paidAt)],
                ["Monto", detail.payment.paymentAmount || "-"],
                ["Referencia SYSWEB", detail.payment.syswebReference || "-"],
                ["Transaccion SYSWEB", detail.payment.syswebTransactionIdTruncated || "-"],
                ["Pulsera", detail.payment.braceletCodeMasked || "-"],
              ]} />
              <div className="mt-4 overflow-x-auto rounded-md border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Intentos</TableHead>
                      <TableHead>Ultimo intento</TableHead>
                      <TableHead>Proximo intento</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.payment.jobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell>{job.jobType}</TableCell>
                        <TableCell>{syswebJobStatusLabel(job.status, job.lastErrorPreview)}</TableCell>
                        <TableCell>{job.attemptCount}/{job.maxAttempts}</TableCell>
                        <TableCell>{formatDate(job.lastAttemptAt)}</TableCell>
                        <TableCell>{formatDate(job.nextAttemptAt)}</TableCell>
                      </TableRow>
                    ))}
                    {detail.payment.jobs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-sm text-gray-600">No hay jobs SYSWEB asociados.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </DetailSection>

            <div className="grid gap-4 lg:grid-cols-2">
              <DetailSection title="5. Credencial / QR">
                <DetailGrid rows={[
                  ["Estado QR", detail.credential.qrStatus.label],
                  ["Credencial enviada", formatDate(detail.credential.credentialSentAt)],
                ]} />
                <Alert className="mt-3 border-blue-200 bg-blue-50 text-blue-900">
                  <Shield className="h-4 w-4" />
                  <AlertDescription>No se muestran tokens QR ni URLs privadas de credencial.</AlertDescription>
                </Alert>
              </DetailSection>

              <DetailSection title="6. Ponencia">
                {detail.paper ? (
                  <DetailGrid rows={[
                    ["Titulo", detail.paper.title || "-"],
                    ["Area", detail.paper.area || "-"],
                    ["Estado registro", statusLabel(detail.paper.registrationStatus)],
                    ["Estado evaluacion", detail.paper.evaluationStatus || "-"],
                    ["Ronda", detail.paper.currentRound ? `Ronda ${detail.paper.currentRound}` : "-"],
                    ["Material final", detail.paper.materialStatus],
                  ]} />
                ) : (
                  <p className="text-sm text-gray-600">Este registro no corresponde a ponente.</p>
                )}
              </DetailSection>
            </div>

            <DetailSection title="7. Timeline operativo">
              <div className="space-y-3">
                {detail.timeline.map((item, index) => (
                  <div key={`${item.label}-${index}`} className="flex gap-3">
                    <div className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#0f2744]">{item.label}</p>
                      <p className="text-xs text-gray-500">{formatDate(item.at)}</p>
                    </div>
                    {item.status && <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">{item.status}</Badge>}
                  </div>
                ))}
              </div>
            </DetailSection>

            <Alert className="border-blue-200 bg-white text-blue-900">
              <Shield className="h-4 w-4" />
              <AlertDescription>Este panel no muestra tokens, magic links, rutas internas, payloads crudos ni URLs de pago.</AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="px-4 text-sm text-gray-600">No se pudo cargar el detalle del registro.</div>
        )}
        <SheetFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-[#0f2744]">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DetailGrid({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label}>
          <p className="text-xs font-semibold uppercase text-gray-500">{label}</p>
          <p className="mt-1 break-words text-sm text-gray-800">{value}</p>
        </div>
      ))}
    </div>
  );
}
